import React from 'react'

import { getIconComponent } from 'component-library/buttons/Sharing/utils/get-icon-component'
import { getButtonOnClick } from 'component-library/buttons/Sharing/utils/get-button-on-click'

import {
    StyledSharing,
    StyledButton,
    StyledIcon,
    FacebookStyledIcon,
} from 'component-library/buttons/Sharing/Sharing.styled'

import { AmpSharing } from 'component-library/buttons/Sharing/Sharing.amp'
import { createRenderTarget } from 'component-library/render-target'
import { addUrlParams } from 'common/helpers/utils'
import { Product } from 'component-library/__product'
import { useSectionMeta } from 'common/routing/use-section-meta'
import { useTheme } from '@emotion/react'
import { ShareOption, SharingEvent } from 'common/events/share-events'

export interface Props {
    /** When the loading flag is passed, the sharing icons will be
     * rendered but not be responsive, even if an invalid url is passed
     */
    loading?: boolean
    url: string
    text: string
    className?: string
    shareOptions: ShareOption[]
    isGhostStyle?: boolean
    isSticky?: boolean
    layout?: 'stacked-md-desktop'
    onEvent: (event: SharingEvent) => void
}

// Render the AMP Sharing component
const AmpSharingTarget: React.FC<Props> = props => {
    const sectionMeta = useSectionMeta()
    const theme = useTheme()

    if (!props.loading && !props.url) {
        return null
    }

    // Define custom AMP Sharing options
    const shareOptions =
        theme.kind === Product.SevenNews
            ? ['facebook', 'twitter', 'email']
            : ['facebook', 'twitter', 'email', 'whatsapp', 'linkedin', 'sms']

    return (
        <StyledSharing className={props.className}>
            {shareOptions.map((shareOption, index) => {
                const shareUrl = addUrlParams(props.url, [
                    ['utm_campaign', 'share-icons'],
                    ['utm_source', shareOption],
                    [
                        'utm_medium',
                        ['facebook', 'twitter'].indexOf(shareOption) !== -1
                            ? 'social'
                            : shareOption,
                    ],
                    ['tid', `${Date.now()}`],
                ])

                const combinedShareMsg = props.text + ' ' + shareUrl

                if (shareOption !== 'clipboard') {
                    return (
                        <AmpSharing
                            key={index}
                            class="amp-sharing-button--rounded" // does not convert className to class, so needs to use class
                            type={shareOption}
                            data-param-text={
                                shareOption === 'whatsapp' ? combinedShareMsg : props.text // whatsapp requires the text and URL in data-param-text
                            }
                            data-param-url={shareUrl}
                            data-param-app_id={
                                shareOption === 'facebook' ? sectionMeta.FacebookAppId : undefined
                            }
                            width={30}
                            height={30}
                        />
                    )
                }

                return
            })}
        </StyledSharing>
    )
}

// Render the web sharing component
const WebSharing: React.FC<Props> = (props: Props) => {
    const sectionMeta = useSectionMeta()
    if (!props.loading && !props.url) {
        return null
    }

    return (
        <StyledSharing className={props.className} isSticky={props.isSticky} layout={props.layout}>
            {props.shareOptions.map((shareOption, index) => {
                const iconComponent = getIconComponent(shareOption)

                const Icon = iconComponent
                    ? shareOption !== 'facebook'
                        ? StyledIcon.withComponent(iconComponent)
                        : FacebookStyledIcon.withComponent(iconComponent)
                    : undefined
                const gtmHook = `gtm-${shareOption}-share-button`

                if (!Icon) {
                    return null
                }
                return (
                    <StyledButton
                        key={shareOption + index}
                        shareOption={shareOption}
                        isGhostStyle={props.isGhostStyle}
                        className={gtmHook}
                        onClick={() => {
                            if (props.loading) {
                                return
                            }

                            props.onEvent({
                                type: 'shareButton.click',
                                originator: 'Sharing',
                                payload: {
                                    shareType: shareOption,
                                },
                            })

                            // We don't need to create these handlers until the user clicks
                            // on the share icon
                            const onClick = getButtonOnClick(
                                shareOption,
                                props.url,
                                props.text,
                                sectionMeta.FacebookAppId
                            )

                            onClick()
                        }}
                    >
                        <Icon />
                    </StyledButton>
                )
            })}
        </StyledSharing>
    )
}
WebSharing.displayName = 'WebSharing'

export const Sharing = createRenderTarget<Props>('Sharing', {
    web: WebSharing,
    amp: AmpSharingTarget,
    rss: null,
    preview: WebSharing,
})
Sharing.displayName = 'Sharing'
